import dayjs, { Dayjs } from 'dayjs';
import { DayOff, SpecialDay, WeekDay } from 'stores/agenda/timeslotsTypes';
import { Patient } from 'stores/patients/patientsTypes';

export interface Timeslot {
  startDate: Dayjs;
  endDate: Dayjs;
}

export const WORK_HOURS = {
  START: 8,
  END: 21,
};

const FORMATS = {
  DEFAULT: 'YYYY-MM-DD',
  TIME: 'YYYY-MM-DD HH:mm',
  FULL_TIME: 'YYYY-MM-DD HH:mm:ss',
  SHORT_TIME: 'YYYY-MM-DD H:mm',
};

export function getMergedTimeslots(timeslots: Timeslot[]): Timeslot[] {
  const clonedSlots = timeslots.map(({ startDate, endDate }) => ({
    startDate: dayjs(startDate),
    endDate: dayjs(endDate),
  }));
  const sortedSlots = clonedSlots.sort((a, b) => {
    const isAfter = a.startDate.isAfter(b.startDate) ? 1 : 0;
    return a.startDate.isBefore(b.startDate) ? -1 : isAfter;
  });

  return sortedSlots.reduce((mergedSlots: Timeslot[], slot) => {
    if (mergedSlots.length === 0) {
      return [slot];
    }

    const currentLastSlot = { ...mergedSlots[mergedSlots.length - 1] };
    if (!slot.startDate.isAfter(currentLastSlot.endDate)) {
      return [
        ...mergedSlots.slice(0, -1),
        {
          startDate: currentLastSlot.startDate,
          endDate: dayjs.max(slot.endDate, currentLastSlot.endDate),
        },
      ];
    }

    return [
      ...mergedSlots,
      slot,
    ];
  }, []);
}

const HARD_LIMIT = 30;

export function weekDaysToTimeslots(
  weekDays: WeekDay[],
  limits: Timeslot = {
    startDate: dayjs(),
    endDate: dayjs().add(14, 'day'),
  },
): Timeslot[] {
  let start = dayjs(limits.startDate).startOf('day');
  const end = dayjs(limits.endDate).endOf('day');
  const sortedWeekDays = structuredClone(weekDays).sort((a, b) => a.dayIndex - b.dayIndex);
  const timeslots: Timeslot[] = [];
  let i = 0;

  while (start.isBefore(end) && i < HARD_LIMIT) {
    const dayIndex = start.get('d');
    const recurrentSlots = sortedWeekDays.filter((weekDay) => weekDay.dayIndex === dayIndex);
    const formatedStart = start.format(FORMATS.DEFAULT);

    recurrentSlots.forEach((weekDay) => {
      weekDay.periodList.forEach((period) => {
        const slotStart = dayjs(`${formatedStart} ${period.startTime}`, FORMATS.FULL_TIME);
        let slotEnd = dayjs(`${formatedStart} ${period.endTime}`, FORMATS.FULL_TIME);
        const min = dayjs(`${formatedStart} ${WORK_HOURS.START}:00`, FORMATS.SHORT_TIME);
        const max = dayjs(`${formatedStart} ${WORK_HOURS.END}:00`, FORMATS.SHORT_TIME);

        if (!slotStart.isBefore(max)) {
          return;
        }

        if (!slotEnd.isAfter(slotStart)) {
          slotEnd = dayjs(max);
        }

        timeslots.push({
          startDate: dayjs.max(slotStart, min),
          endDate: dayjs.min(slotEnd, max),
        });
      });
    });
    start = start.add(1, 'day');
    i += 1;
  }

  return timeslots;
}

export function specialDaysToTimeslots(specialDays: SpecialDay[]): Timeslot[] {
  const timeslots: Timeslot[] = [];

  specialDays.forEach(({ startDate, endDate, periodList }) => {
    periodList.forEach(({ startTime, endTime }) => {
      let start = dayjs.max(
        dayjs(`${startDate} ${startTime}`, FORMATS.FULL_TIME),
        dayjs(`${startDate} ${WORK_HOURS.START}:00`, FORMATS.SHORT_TIME),
      );
      const end = dayjs.min(
        dayjs(`${endDate} ${endTime}`, FORMATS.FULL_TIME),
        dayjs(`${endDate} ${WORK_HOURS.END}:00`, FORMATS.SHORT_TIME),
      );

      while (start.isBefore(end)) {
        timeslots.push({
          startDate: dayjs(start),
          endDate: dayjs.min(
            dayjs(`${start.format(FORMATS.DEFAULT)} ${WORK_HOURS.END}:00`, FORMATS.SHORT_TIME),
            dayjs(end),
          ),
        });
        start = start.add(1, 'day').set('hour', WORK_HOURS.START).set('minute', 0);
      }
    });
  });

  return timeslots;
}

export function patientsToTimeslots(patients: Patient[]): Timeslot[] {
  const timeslots: Timeslot[] = [];

  patients.forEach((patient) => {
    if (
      Boolean(patient.rdv_3___date)
      && Boolean(patient.rdv_3___heure)
      && Boolean(patient.rdv_3__duration)
    ) {
      const startDate = dayjs(`${patient.rdv_3___date} ${patient.rdv_3___heure}`, FORMATS.TIME);
      if (startDate.isValid()) {
        timeslots.push({
          startDate,
          endDate: startDate.add(patient.rdv_3__duration, 'seconds'),
        });
      }
    }
    if (
      Boolean(patient.rdv_suivi___date)
      && Boolean(patient.rdv_suivi___heure)
      && Boolean(patient.rdv_suivi__duration)
    ) {
      const startDate = dayjs(
        `${patient.rdv_suivi___date} ${patient.rdv_suivi___heure}`,
        FORMATS.TIME,
      );
      if (startDate.isValid()) {
        timeslots.push({
          startDate,
          endDate: startDate.add(patient.rdv_suivi__duration || 0, 'seconds'),
        });
      }
    }
  });

  return timeslots;
}

export interface SlotDurations {
  availableDuration: number;
  numberOfMeetingOutsideSlots: number;
  meetingDuration: number;
}

export function getTotalMinutesSlotsDurations(
  weekDays: WeekDay[],
  specialDays: SpecialDay[],
  offDays: DayOff[],
  meetings: Patient[],
  limits: Timeslot = {
    startDate: dayjs(),
    endDate: dayjs().add(14, 'day'),
  },
): SlotDurations {
  const weekSlots = weekDaysToTimeslots(weekDays, limits);
  const specialSlots = specialDaysToTimeslots(specialDays);
  const mergedSlots = getMergedTimeslots([...weekSlots, ...specialSlots])
    .filter(({ startDate, endDate }) => (
      startDate.isAfter(dayjs(limits.startDate).startOf('day'))
        && endDate.isBefore(dayjs(limits.endDate).endOf('day'))
    ))
    .filter(({ startDate, endDate }) => offDays.every((dayOff) => !(
      startDate.isAfter(dayjs(dayOff.startDate).startOf('day'))
        && endDate.isBefore(dayjs(dayOff.endDate).endOf('day'))
    )));
  const meetingSlots = getMergedTimeslots(patientsToTimeslots(meetings))
    .filter(({ startDate, endDate }) => (
      startDate.isAfter(dayjs(limits.startDate).startOf('day'))
        && endDate.isBefore(dayjs(limits.endDate).endOf('day'))
    )).sort((a, b) => {
      const isAfter = a.startDate.isAfter(b.startDate) ? 1 : 0;
      return a.startDate.isBefore(b.startDate) ? -1 : isAfter;
    });
  let numberOfMeetingOutsideSlots = 0;
  const meetingsInAvaialbleSlots = meetingSlots.filter(({ startDate, endDate }) => {
    const isInSlot = mergedSlots.some((slot) => (
      !startDate.isBefore(slot.startDate) && !endDate.isAfter(slot.endDate)
    ));

    if (!isInSlot) {
      numberOfMeetingOutsideSlots += 1;
    }

    return isInSlot;
  });

  return {
    availableDuration: mergedSlots.reduce(
      (sum, slot) => sum + slot.endDate.diff(slot.startDate, 'minute'),
      0,
    ),
    numberOfMeetingOutsideSlots,
    meetingDuration: meetingsInAvaialbleSlots.reduce(
      (sum, slot) => sum + slot.endDate.diff(slot.startDate, 'minute'),
      0,
    ),
  };
}
